/**
 * PageHeader - Enhetlig sidhuvud för rapporter och sidor
 * Titel, valfri beskrivning, ikon, åtgärdsknappar och tillbaka-pil
 */

import React, { useRef } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import ModuleGuideTour, { type ModuleGuideTourHandle } from '@/components/guide/ModuleGuideTour';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from './PageBackButton';

export interface PageHeaderProps {
  /** Sidans titel */
  title: string;
  /** Valfri beskrivning under titeln */
  description?: string;
  /** Valfri ikon (t.ex. Heroicon) */
  icon?: React.ReactNode;
  /** Valfria åtgärder (t.ex. export-knapp) */
  actions?: React.ReactNode;
  /** Dölj tillbaka-pilen (default: false) */
  hideBack?: boolean;
  /** Fast route att navigera till (ersätter navigate(-1)) */
  backTo?: string;
  /** Egen bakåt-hanterare (t.ex. ett steg i drill-down). Har företräde framför backTo/navigate(-1). */
  onBack?: () => void;
  /** Ytterligare CSS-klasser på wrapper */
  className?: string;
  /** Tour-nyckel för modulspecifik guide. Om angiven visas "?"-knapp. */
  tourKey?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  icon,
  actions,
  hideBack = false,
  backTo,
  onBack,
  className = '',
  tourKey,
}) => {
  const { t } = useTranslation();
  const tourRef = useRef<ModuleGuideTourHandle>(null);

  return (
    <>
      {tourKey && <ModuleGuideTour ref={tourRef} tourKey={tourKey} autoStart={true} />}
      <div
        className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 ${className}`}
      >
        <div className="flex items-center gap-3">
          {!hideBack && <PageBackButton onBack={onBack} backTo={backTo} />}
          <div>
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              {icon}
              {title}
            </h1>
            {description && (
              <p className="text-textSecondary mt-1">{description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {tourKey && (
            <button
              onClick={() => tourRef.current?.startTour()}
              title={t('guide.showGuide')}
              className="p-2 rounded-lg text-textSecondary hover:text-textDefault hover:bg-bgElevated transition-colors"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};
